import React, { useEffect, useRef } from 'react';
import { SharedEffects } from './SharedEffects';

interface Node {
  x: number;
  y: number;
  vx: number;
  vy: number;
  radius: number;
  baseOpacity: number;
  activation: number;
}

interface Pulse {
  fromIndex: number;
  toIndex: number;
  progress: number;
  speed: number;
}

export function NeuralNetworkOverlay() {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let animationId: number;
    let width = 0;
    let height = 0;

    const resize = () => {
      width = window.innerWidth;
      height = window.innerHeight;
      canvas.width = width;
      canvas.height = height;
    };
    resize();
    window.addEventListener('resize', resize);

    const nodeCount = 35;
    const connectionDistance = 220;
    const nodes: Node[] = [];

    for (let i = 0; i < nodeCount; i++) {
      nodes.push({
        x: Math.random() * width,
        y: Math.random() * height,
        vx: (Math.random() - 0.5) * 0.15,
        vy: (Math.random() - 0.5) * 0.15,
        radius: Math.random() * 1.5 + 1.5,
        baseOpacity: Math.random() * 0.15 + 0.15,
        activation: 0,
      });
    }

    const pulses: Pulse[] = [];
    let pulseTimer = 0;

    const draw = () => {
      ctx.clearRect(0, 0, width, height);

      nodes.forEach((node) => {
        node.x += node.vx;
        node.y += node.vy;
        if (node.x < -40) node.vx = Math.abs(node.vx);
        if (node.x > width + 40) node.vx = -Math.abs(node.vx);
        if (node.y < -40) node.vy = Math.abs(node.vy);
        if (node.y > height + 40) node.vy = -Math.abs(node.vy);
        node.activation *= 0.97;
      });

      pulseTimer++;
      if (pulseTimer > 90) {
        pulseTimer = 0;
        const sourceIndex = Math.floor(Math.random() * nodeCount);
        const source = nodes[sourceIndex];
        source.activation = 1;
        nodes.forEach((target, ti) => {
          if (ti === sourceIndex) return;
          const dx = target.x - source.x;
          const dy = target.y - source.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < connectionDistance && Math.random() > 0.4) {
            pulses.push({ fromIndex: sourceIndex, toIndex: ti, progress: 0, speed: 0.008 + Math.random() * 0.006 });
          }
        });
      }

      for (let i = 0; i < nodeCount; i++) {
        for (let j = i + 1; j < nodeCount; j++) {
          const a = nodes[i];
          const b = nodes[j];
          const dx = b.x - a.x;
          const dy = b.y - a.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < connectionDistance) {
            const opacity = (1 - dist / connectionDistance) * 0.12;
            const activationBoost = Math.max(a.activation, b.activation) * 0.15;
            ctx.beginPath();
            ctx.moveTo(a.x, a.y);
            ctx.lineTo(b.x, b.y);
            ctx.strokeStyle = `rgba(66, 133, 244, ${opacity + activationBoost})`;
            ctx.lineWidth = 0.6;
            ctx.stroke();
          }
        }
      }

      for (let i = pulses.length - 1; i >= 0; i--) {
        const pulse = pulses[i];
        pulse.progress += pulse.speed;
        if (pulse.progress >= 1) {
          nodes[pulse.toIndex].activation = Math.min(1, nodes[pulse.toIndex].activation + 0.6);
          pulses.splice(i, 1);
          continue;
        }
        const from = nodes[pulse.fromIndex];
        const to = nodes[pulse.toIndex];
        const px = from.x + (to.x - from.x) * pulse.progress;
        const py = from.y + (to.y - from.y) * pulse.progress;

        ctx.beginPath();
        ctx.arc(px, py, 6, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(66, 133, 244, 0.12)';
        ctx.shadowColor = 'rgba(66, 133, 244, 0.4)';
        ctx.shadowBlur = 10;
        ctx.fill();
        ctx.shadowBlur = 0;
        ctx.beginPath();
        ctx.arc(px, py, 2, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(66, 133, 244, 0.7)';
        ctx.fill();
      }

      nodes.forEach((node) => {
        const glowOpacity = node.baseOpacity * 0.3 + node.activation * 0.3;
        const coreOpacity = node.baseOpacity + node.activation * 0.5;
        const glowRadius = node.radius * 3 + node.activation * 8;

        ctx.beginPath();
        ctx.arc(node.x, node.y, glowRadius, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(66, 133, 244, ${glowOpacity * 0.15})`;
        ctx.fill();
        ctx.beginPath();
        ctx.arc(node.x, node.y, node.radius + 3 + node.activation * 4, 0, Math.PI * 2);
        ctx.strokeStyle = `rgba(66, 133, 244, ${glowOpacity * 0.3})`;
        ctx.lineWidth = 0.5;
        ctx.stroke();
        ctx.beginPath();
        ctx.arc(node.x, node.y, node.radius + node.activation * 1.5, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(66, 133, 244, ${coreOpacity})`;
        ctx.fill();

        if (node.activation > 0.3) {
          ctx.beginPath();
          ctx.arc(node.x, node.y, node.radius * 0.5, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(255, 255, 255, ${node.activation * 0.5})`;
          ctx.fill();
        }
      });

      animationId = requestAnimationFrame(draw);
    };
    draw();

    return () => {
      cancelAnimationFrame(animationId);
      window.removeEventListener('resize', resize);
    };
  }, []);

  return (
    <div className="auth-overlay">
      <canvas ref={canvasRef} />
      <SharedEffects />
    </div>
  );
}
